import React, { useEffect, useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';

const maplibre = window.maplibregl;

function createCircle(center, radiusMeters) {
  const points = 64;
  const coords = [];
  for (let i = 0; i < points; i++) {
    const angle = (i * 360) / points;
    const dx = radiusMeters * Math.cos((angle * Math.PI) / 180);
    const dy = radiusMeters * Math.sin((angle * Math.PI) / 180);
    const latOffset = dy / 111320;
    const lngOffset = dx / (111320 * Math.cos((center[1] * Math.PI) / 180));
    coords.push([center[0] + lngOffset, center[1] + latOffset]);
  }
  coords.push(coords[0]);
  return {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [coords] }
  };
}

/**
 * Admin/create-zone style radius picker.
 * center: [lng, lat]
 */
export default function MapRadiusPicker({
  center,
  zoom = 10.5,
  radius = 10,
  onCenterChange,
  onZoomChange,
  onRadiusChange,
  onBack,
  onContinue,
  continueLabel = 'Save',
  busy = false
}) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markerRef = useRef(null);
  const isDragging = useRef(false);
  const currentRadius = useRef(radius);

  useEffect(() => {
    if (!mapContainer.current || map.current || !maplibre) return undefined;

    map.current = new maplibre.Map({
      container: mapContainer.current,
      style: 'https://api.maptiler.com/maps/5e93952d-a638-4302-8ab2-bab246582ff4/style.json?key=UHHUx3iqEIPjCVMQT4MD',
      center,
      zoom
    });

    map.current.on('load', () => {
      const circleGeoJSON = createCircle(center, currentRadius.current * 1609.34);
      map.current.addSource('radius-circle', { type: 'geojson', data: circleGeoJSON });
      map.current.addLayer({
        id: 'radius-circle-fill',
        type: 'fill',
        source: 'radius-circle',
        paint: { 'fill-color': '#B5504D', 'fill-opacity': 0.2 }
      });
      map.current.addLayer({
        id: 'radius-circle-stroke',
        type: 'line',
        source: 'radius-circle',
        paint: { 'line-color': '#B5504D', 'line-width': 2, 'line-opacity': 0.8 }
      });

      const el = document.createElement('div');
      el.style.width = '24px';
      el.style.height = '24px';
      el.style.borderRadius = '50%';
      el.style.backgroundColor = '#B5504D';
      el.style.border = '4px solid white';
      el.style.cursor = 'grab';
      el.style.boxShadow = '0 3px 6px rgba(0,0,0,0.4)';

      markerRef.current = new maplibre.Marker({ element: el, draggable: true })
        .setLngLat(center)
        .addTo(map.current);

      markerRef.current.on('dragstart', () => {
        isDragging.current = true;
      });
      markerRef.current.on('drag', () => {
        const c = markerRef.current.getLngLat();
        updateCircle([c.lng, c.lat], currentRadius.current);
      });
      markerRef.current.on('dragend', () => {
        isDragging.current = false;
        const c = markerRef.current.getLngLat();
        onCenterChange?.([c.lng, c.lat]);
      });

      map.current.on('zoom', () => {
        if (isDragging.current) return;
        const bounds = map.current.getBounds();
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const c = markerRef.current.getLngLat();
        const latMiles = (Math.abs(ne.lat - sw.lat) / 2) * 69;
        const lngMiles = (Math.abs(ne.lng - sw.lng) / 2) * 69 * Math.cos((c.lat * Math.PI) / 180);
        let newRadius = Math.round(Math.min(latMiles, lngMiles));
        newRadius = Math.max(1, Math.min(100, newRadius));
        currentRadius.current = newRadius;
        onRadiusChange?.(newRadius);
        updateCircle([c.lng, c.lat], newRadius);
      });

      map.current.on('zoomend', () => {
        if (!isDragging.current) onZoomChange?.(map.current.getZoom());
      });
    });

    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, []);

  const updateCircle = (centerCoords, radiusMiles) => {
    if (!map.current?.getSource('radius-circle')) return;
    map.current.getSource('radius-circle').setData(
      createCircle(centerCoords, radiusMiles * 1609.34)
    );
  };

  useEffect(() => {
    currentRadius.current = radius;
    if (markerRef.current && map.current?.getSource('radius-circle')) {
      const c = markerRef.current.getLngLat();
      updateCircle([c.lng, c.lat], radius);
    }
  }, [radius]);

  return (
    <Box>
      <Box ref={mapContainer} sx={{ width: '100%', height: '55vh', borderRadius: 1, overflow: 'hidden', border: '2px solid #ccc' }} />
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="body1" fontWeight="bold">Search Radius: {radius} miles</Typography>
          <Typography variant="caption" color="text.secondary">
            Drag the red dot to move center • Zoom to adjust radius
          </Typography>
        </Box>
        <Box>
          {onBack && <Button onClick={onBack} sx={{ mr: 1 }}>Back</Button>}
          {onContinue && (
            <Button variant="contained" onClick={onContinue} disabled={busy} sx={{ backgroundColor: '#202E40' }}>
              {busy ? 'Saving...' : continueLabel}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}
